import React, { useState } from "react";
import {
  Box,
  ClickAwayListener,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  TypographyProps,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { colors } from "../../../styles/theme";

interface EditableLabelProps {
  value: string;
  onSave: (value: string) => void;
  /** Ob der Wert überhaupt bearbeitet werden darf (kombiniert i.d.R. isEditable && canEdit). */
  editable?: boolean;
  /**
   * 'click'   – Text selbst ist die Klickfläche, dezenter Hover-Hintergrund (Standard)
   * 'pencil'  – Stift-Icon erscheint erst bei Hover über die Zeile, klickbar
   */
  trigger?: "click" | "pencil";
  variant?: TypographyProps["variant"];
  minWidth?: number;
  /** Verhindert, dass der Klick an eine übergeordnete klickbare Zeile durchgereicht wird. */
  stopPropagation?: boolean;
}

/**
 * Inline-editierbares Label: Klick/Stift wechselt zu einem Textfeld, Enter/Blur speichert,
 * Escape verwirft. Gemeinsame Basis für die bisher mehrfach duplizierten Inline-Edit-Muster
 * in den Kosten- und Genehmigungs-Sections.
 */
const EditableLabel: React.FC<EditableLabelProps> = ({
  value,
  onSave,
  editable = true,
  trigger = "click",
  variant = "body1",
  minWidth,
  stopPropagation = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const stop = (e: React.SyntheticEvent) => {
    if (stopPropagation) e.stopPropagation();
  };

  const startEdit = (e: React.SyntheticEvent) => {
    stop(e);
    if (!editable) return;
    setEditValue(value);
    setIsEditing(true);
  };

  const handleSave = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== value) {
      onSave(trimmed);
    } else {
      setEditValue(value);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    stop(e);
    if (e.key === "Enter") handleSave();
    else if (e.key === "Escape") {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  if (!editable && trigger === "click") {
    return <Typography variant={variant}>{value}</Typography>;
  }

  if (isEditing) {
    return (
      <ClickAwayListener onClickAway={handleSave}>
        <TextField
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onClick={stop}
          autoFocus
          size="small"
          variant="standard"
          sx={{
            flex: 1,
            minWidth,
            "& .MuiInput-input": {
              fontSize: variant === "h6" ? "1.1rem" : "1rem",
              fontWeight: variant === "h6" ? 500 : undefined,
              py: variant === "h6" ? 0.25 : 0.5,
            },
          }}
        />
      </ClickAwayListener>
    );
  }

  if (trigger === "pencil") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          flex: 1,
          "&:hover .editable-label-pencil": { opacity: 1 },
        }}
      >
        <Typography
          variant={variant}
          sx={{
            fontWeight: 500,
            fontSize: "1.1rem",
            color: "inherit",
            lineHeight: 1.3,
          }}
        >
          {value}
        </Typography>
        {editable && (
          <Tooltip title="Umbenennen">
            <IconButton
              className="editable-label-pencil"
              size="small"
              onClick={startEdit}
              sx={{ opacity: 0, transition: "opacity 0.15s", ml: 0.5, p: 0.25 }}
            >
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }

  return (
    <Typography
      variant={variant}
      onClick={startEdit}
      sx={{
        flex: 1,
        minWidth,
        cursor: editable ? "pointer" : "default",
        "&:hover": editable
          ? { backgroundColor: colors.opacity.black04, borderRadius: 1 }
          : {},
        px: editable ? 1 : 0,
        py: 0.5,
        transition: "background-color 0.2s",
      }}
    >
      {value}
    </Typography>
  );
};

export default EditableLabel;
